/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { isArray } from '@ember/array';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type Goal from '@linc-technologies/linced-common/models/goals/goal';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

// GradeMeta is one grade option from goals/subject possibleGrades or
// defaultGrades.
interface GradeMeta {
  value: number;
  string?: string;
  colourClass?: string;
}

// Proto: goals.State.
export default class State extends Model {
  declare [Type]: 'goals/state';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  // Pairs with goals/goal.states. linced-admin replaces goals/goal with a model
  // that has no `states`, so it overrides this relationship with inverse: null
  // in its own goals/state -- see linced-admin/app/models/goals/state.js.
  @belongsTo('goals/goal', { async: true, inverse: 'states' })
  declare goalId: AsyncBelongsTo<Goal>;

  @alias('goalId')
  declare goal: AsyncBelongsTo<Goal>;

  @alias('goal.id')
  declare goal_id: string | null | undefined;

  @alias('goal.subject_id')
  declare goal_subject_id: unknown;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare assessorId: AsyncBelongsTo<Person>;

  @alias('assessorId')
  declare assessor: AsyncBelongsTo<Person>;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @attr('number', { defaultValue: 0 })
  declare awarded: number;

  @attr('string', { defaultValue: '' })
  declare reason: string;

  // Grades are 1 to 5. 0 means no grade.
  @attr('number', { defaultValue: 0 })
  declare grade: number;

  @computed(
    'grade',
    'goal.possibleGrades.[]',
    'goal.subject.{possibleGrades.[],defaultGrades.[]}',
  )
  get gradeMeta() {
    if (!this.grade) {
      return;
    }

    const possibleGrades = this.get('goal.possibleGrades') as
      GradeMeta[] | undefined;
    const subjectGrades = this.get('goal.subject.possibleGrades') as
      GradeMeta[] | undefined;
    const defaultGrades = this.get('goal.subject.defaultGrades') as
      GradeMeta[] | undefined;
    let gradeMeta: GradeMeta | undefined;

    if (isArray(possibleGrades)) {
      gradeMeta = findBy(possibleGrades, 'value', this.grade);
    }
    if (isEmpty(gradeMeta) && isArray(subjectGrades)) {
      gradeMeta = findBy(subjectGrades, 'value', this.grade);
    }
    if (isEmpty(gradeMeta) && isArray(defaultGrades)) {
      gradeMeta = findBy(defaultGrades, 'value', this.grade);
    }
    return gradeMeta;
  }

  @computed('grade', 'gradeMeta')
  get gradeLabel() {
    if (!isEmpty(this.gradeMeta) && !isEmpty(this.gradeMeta!.string)) {
      return this.gradeMeta!.string;
    }

    return this.grade || undefined;
  }

  @computed('grade', 'gradeMeta')
  get gradeClass() {
    if (!isEmpty(this.gradeMeta) && !isEmpty(this.gradeMeta!.colourClass)) {
      return this.gradeMeta!.colourClass;
    }

    return '';
  }
}
